/**
 * imageLabPrompt preset — voice input for creating a new AI image template
 * in ImageLab. Turns a spoken visual brief into every field the ImageLab
 * editor needs, INCLUDING an intelligent engine (model) pick that would
 * otherwise force the user to reason about six generator lanes and their
 * trade-offs.
 *
 * Schema type: 'fields'
 *   Extracts the exact keys the ImageLab aiDraft state uses so the caller
 *   can drop the values in with a single spread:
 *     - name       (string)          — short human name for the template
 *     - token      (string)          — optional; if empty the caller slugifies name
 *     - prompt     (long_string)     — the full generator prompt with {{variables}}
 *     - aspect     (enum)            — square | landscape | portrait
 *     - sourceMode (enum)            — prompt | website  (website only when
 *                                       user asked for a redesign / before-after
 *                                       of the recipient's live site)
 *     - model      (enum, 6 lanes)   — smart pick based on intent (see hint)
 *     - quality    (enum)            — low | medium | high (OpenAI-only)
 *     - altText    (string)          — accessibility fallback
 *
 * The six guiding questions in intro.questions map onto these fields in a
 * loose 1:1 — the user can answer any order and skip anything they don't
 * care about. Everything left blank simply keeps the ImageLab default.
 *
 * MODEL PICKING PHILOSOPHY (encoded in the field.hint below):
 *   - infographic / analysis / chart with real data or precise labels
 *     → gemini-3-pro-image (Nano Banana Pro — grounded text, correct numbers)
 *   - website redesign / before-after
 *     → gemini-3-pro-image with sourceMode='website'
 *   - photoreal marketing image or complex creative
 *     → flux-2-pro (EU-hosted, top value) OR gemini-3.1-flash-lite-image
 *   - simple illustration / cheapest lane
 *     → gpt-image-1-mini
 *   - dense typography / poster-style layout
 *     → gpt-image-2
 *   - Uncertain → gemini-3.1-flash-lite-image (Nano Banana — best general value).
 */

import React, { useEffect, useMemo, useState } from 'react';

const ASPECT_OPTIONS = [
  { value: 'square',    label: 'Square (1:1)' },
  { value: 'landscape', label: 'Landscape (3:2)' },
  { value: 'portrait',  label: 'Portrait (2:3)' },
];

const SOURCE_MODE_OPTIONS = [
  { value: 'prompt',  label: 'Text prompt (+ optional uploaded base image)' },
  { value: 'website', label: 'Recipient\'s live website (auto-screenshot per contact)' },
];

// Same order/labels as ImageLab.js MODEL_OPTIONS so the preview select feels
// identical to the manual editor's engine selector.
const MODEL_OPTIONS = [
  { value: 'gpt-image-1-mini',            label: 'Budget: cheapest, simple visuals (OpenAI)' },
  { value: 'gemini-3.1-flash-lite-image', label: 'Nano Banana: best value for creative (Google)' },
  { value: 'flux-2-pro',                  label: 'FLUX.2 Pro: EU-hosted, top value for photoreal & redesigns (BFL)' },
  { value: 'flux-2-max',                  label: 'FLUX.2 Max: EU-hosted, highest quality & best edit consistency (BFL)' },
  { value: 'gemini-3-pro-image',          label: 'Nano Banana Pro: best for analyses & infographics (Google)' },
  { value: 'gpt-image-2',                 label: 'Premium: sharpest text & layout (OpenAI)' },
];

const QUALITY_OPTIONS = [
  { value: 'low',    label: 'Low (cheapest)' },
  { value: 'medium', label: 'Medium' },
  { value: 'high',   label: 'High' },
];

// ─── Preview component ──────────────────────────────────────────────────
/**
 * Compact 2-column preview so the user can eyeball what the AI extracted
 * before it drops into the ImageLab editor. The prompt textarea is the
 * hero (it's the field the user cares most about); everything else is
 * secondary and lives in a tight grid below.
 */
function ImageLabPreview({ structured, onConfirm, onCancel, saving }) {
  const initial = structured?.fields || {};
  const isOpenAiModel = useMemo(
    () => (m) => m === 'gpt-image-1-mini' || m === 'gpt-image-2',
    []
  );

  const [values, setValues] = useState({
    name:       initial.name       || '',
    prompt:     initial.prompt     || '',
    aspect:     initial.aspect     || 'square',
    sourceMode: initial.sourceMode || 'prompt',
    model:      initial.model      || 'gemini-3.1-flash-lite-image',
    quality:    initial.quality    || 'low',
    altText:    initial.altText    || '',
    // We do NOT expose token here — the ImageLab caller slugifies the name
    // automatically (with clash-detection). Showing a token field would
    // duplicate what the editor already handles.
  });

  useEffect(() => {
    if (initial) {
      setValues({
        name:       initial.name       || '',
        prompt:     initial.prompt     || '',
        aspect:     initial.aspect     || 'square',
        sourceMode: initial.sourceMode || 'prompt',
        model:      initial.model      || 'gemini-3.1-flash-lite-image',
        quality:    initial.quality    || 'low',
        altText:    initial.altText    || '',
      });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [structured?.fields?.prompt]);

  const update = (k, v) => setValues((prev) => ({ ...prev, [k]: v }));
  const promptLen = values.prompt.length;
  const canConfirm = values.prompt.trim().length >= 40 && values.name.trim().length > 0;

  return (
    <div className="speakEngine__body speakEngine__body--preview">
      <div className="speakEngine__previewHead">
        <div>
          <h3>AI image template ready</h3>
          {structured?.summary && <p className="speakEngine__previewSummary">{structured.summary}</p>}
        </div>
      </div>

      <div className="speakEngine__fieldsGrid">
        <label className="speakEngine__field speakEngine__field--wide">
          <span className="speakEngine__fieldLabel">
            Image prompt
            <span className={`speakEngine__fieldCounter ${promptLen < 40 ? 'is-low' : ''}`}>
              {promptLen} chars {promptLen < 40 && '· needs more detail'}
            </span>
          </span>
          <textarea
            className="speakEngine__fieldTextarea"
            rows={7}
            value={values.prompt}
            onChange={(e) => update('prompt', e.target.value)}
            placeholder="The full generator prompt. Use {{business_name}}, {{lead_city}}, {{lead_type}} for per-recipient personalisation."
          />
        </label>

        <div className="speakEngine__fieldsRow">
          <label className="speakEngine__field">
            <span className="speakEngine__fieldLabel">Template name</span>
            <input
              type="text"
              className="speakEngine__fieldInput"
              value={values.name}
              onChange={(e) => update('name', e.target.value)}
              placeholder="e.g. Storefront hero"
            />
          </label>
          <label className="speakEngine__field">
            <span className="speakEngine__fieldLabel">Shape</span>
            <select
              className="speakEngine__fieldInput"
              value={values.aspect}
              onChange={(e) => update('aspect', e.target.value)}
            >
              {ASPECT_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </label>
        </div>

        <div className="speakEngine__fieldsRow">
          <label className="speakEngine__field">
            <span className="speakEngine__fieldLabel">Engine (AI model)</span>
            <select
              className="speakEngine__fieldInput"
              value={values.model}
              onChange={(e) => update('model', e.target.value)}
            >
              {MODEL_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </label>
          <label className="speakEngine__field">
            <span className="speakEngine__fieldLabel">Quality (OpenAI only)</span>
            <select
              className="speakEngine__fieldInput"
              value={values.quality}
              onChange={(e) => update('quality', e.target.value)}
              disabled={!isOpenAiModel(values.model)}
            >
              {QUALITY_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </label>
        </div>

        <label className="speakEngine__field">
          <span className="speakEngine__fieldLabel">Image source</span>
          <select
            className="speakEngine__fieldInput"
            value={values.sourceMode}
            onChange={(e) => update('sourceMode', e.target.value)}
          >
            {SOURCE_MODE_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        </label>

        <label className="speakEngine__field">
          <span className="speakEngine__fieldLabel">Alt text (accessibility & fallback)</span>
          <input
            type="text"
            className="speakEngine__fieldInput"
            value={values.altText}
            onChange={(e) => update('altText', e.target.value)}
            placeholder='e.g. "Storefront mockup for {{business_name}}"'
          />
        </label>
      </div>

      <div className="speakEngine__previewActions">
        <button type="button" className="speakEngine__btn speakEngine__btn--ghost" onClick={onCancel} disabled={saving}>
          Back to transcript
        </button>
        <button
          type="button"
          className="speakEngine__btn speakEngine__btn--primary"
          onClick={() => onConfirm(values)}
          disabled={saving || !canConfirm}
          title={!canConfirm ? 'The prompt is too short or the name is empty.' : ''}
        >
          {saving
            ? <><span className="speakEngine__btnSpinner" aria-hidden /> Opening editor…</>
            : <>Open in editor</>}
        </button>
      </div>
    </div>
  );
}

// ─── Preset export ──────────────────────────────────────────────────────
const imageLabPromptPreset = {
  id: 'imageLabPrompt',
  title: 'Describe your image',
  subtitle: 'AI writes the prompt, picks the engine, and pre-fills the editor.',
  intro: {
    heading: 'Tap and describe the image',
    hint: 'Speak freely for 30–90 seconds. Loosely follow the six prompts below. Everything you skip keeps the Image Lab default.',
    questionsHeading: 'Guiding questions',
    questions: [
      'What should the image show? Describe the scene like you\'re briefing a designer.',
      'Who\'s it going to? (industry, tone, region. This shapes style and language.)',
      'Should it be personalised per recipient? Which fields? (company name, city, business type, website…)',
      'Style: photorealistic photo, illustration, product mockup, data chart / infographic, or a website redesign?',
      'Shape: square (inline / social), landscape (hero banner), or portrait (mobile-first)?',
      'Any exact text or labels that MUST appear inside the image?',
    ],
  },
  organizeCta: 'Draft the image template',
  organizingText: 'AI is writing the generator prompt, picking the right engine for your intent, and setting up shape / alt text. Usually 3–8 seconds.',
  doneText: () => 'Template drafted. Review and save.',
  schema: {
    type: 'fields',
    fields: [
      {
        id: 'name',
        label: 'Template name',
        type: 'string',
        required: true,
        hint: 'Short human name for this template (3–4 words max). Examples: "Storefront hero", "SEO chart", "Website redesign mockup". Derive from the scene the user described.',
      },
      {
        id: 'token',
        label: 'Placeholder token',
        type: 'string',
        hint: 'Optional. Lowercase snake_case slug of the name (e.g. "storefront_hero"). Leave "" if not clearly stated — the caller slugifies from the name automatically.',
      },
      {
        id: 'prompt',
        label: 'Image generator prompt',
        type: 'long_string',
        required: true,
        hint: 'A single detailed paragraph (no bullet lists, no headings) that describes the image to the generator model. Write in polished English regardless of the transcript language. MUST reference recipient personalisation variables where the user asked for them — pick from: {{business_name}}, {{lead_name}}, {{first_name}}, {{lead_city}}, {{lead_country}}, {{lead_type}}, {{lead_website}}. Include exact text/labels the user wants inside the image (quote them). Mention the style (photorealistic / illustration / mockup / infographic / chart / redesign), lighting, composition, background — but stay concise. If the user did not name variables, still add {{business_name}} where a company name would sensibly appear.',
      },
      {
        id: 'aspect',
        label: 'Aspect ratio',
        type: 'enum',
        options: ['square', 'landscape', 'portrait'],
        hint: 'Pick the shape based on WHERE the image lives. "landscape" for hero banners / website mockups / data charts / before-after. "portrait" only if user said mobile-first or vertical. "square" for inline / social / anything ambiguous.',
      },
      {
        id: 'sourceMode',
        label: 'Image source',
        type: 'enum',
        options: ['prompt', 'website'],
        hint: 'Set to "website" ONLY if the user explicitly wants a redesign, before/after, or overlay of the RECIPIENT\'S actual live website (their real logo/colors captured per contact). Otherwise "prompt".',
      },
      {
        id: 'model',
        label: 'AI engine',
        type: 'enum',
        options: ['gpt-image-1-mini', 'gemini-3.1-flash-lite-image', 'flux-2-pro', 'flux-2-max', 'gemini-3-pro-image', 'gpt-image-2'],
        hint: `SMART engine pick — read the whole hint before choosing:
- Choose "gemini-3-pro-image" when the image is a data chart, infographic, analysis, or contains multiple pieces of specific text/numbers. This model has grounded typography — labels & numbers come out correct.
- Choose "gemini-3-pro-image" ALSO when sourceMode == "website" (website redesign / before-after) — it handles edit consistency of the recipient's real branding best.
- Choose "flux-2-pro" for photorealistic marketing shots (storefronts, people, product hero shots) unless the user asked for the cheapest option. EU-hosted, GDPR-friendly, sharpest photoreal.
- Choose "flux-2-max" only when the user explicitly asked for MAX / highest / premium quality on photoreal content.
- Choose "gemini-3.1-flash-lite-image" (Nano Banana) as the DEFAULT for any creative / illustrative / stylised image, and whenever intent is unclear. Best general value.
- Choose "gpt-image-1-mini" only if the user asked for the cheapest / simplest / budget lane.
- Choose "gpt-image-2" only when the image is a dense typographic poster / layout where OpenAI's text rendering matters more than photoreal or grounding.
When uncertain: gemini-3.1-flash-lite-image.`,
      },
      {
        id: 'quality',
        label: 'Quality',
        type: 'enum',
        options: ['low', 'medium', 'high'],
        hint: 'Only meaningful when model is gpt-image-1-mini or gpt-image-2 (OpenAI). Default "low" for cost — bump to "medium" or "high" only if the user asked for higher quality or if the image is a hero / conversion-critical banner. Non-OpenAI models ignore this.',
      },
      {
        id: 'altText',
        label: 'Alt text',
        type: 'string',
        hint: 'Short accessibility / spam-fallback description of what the image shows. Include {{business_name}} (or the most obvious recipient variable) so it stays personalised. Example: "SEO visibility trend for {{business_name}}".',
      },
    ],
  },
  PreviewComponent: ImageLabPreview,
};

export default imageLabPromptPreset;
